import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { m, useAnimationFrame } from "framer-motion";

// @mui
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";

// components
import Iconify from "src/components/iconify";
import { useLocales } from "src/locales";
import { useResponsive } from "src/hooks/use-responsive";

// ----------------------------------------------------------------------

const BAR_COUNT = 28;
const BAR_COUNT_MOBILE = 14;

// About 16 samples a second: a natural scroll speed for the waveform, and 16 style writes per bar per
// second instead of 60.
const SAMPLE_INTERVAL_MS = 60;

// The idle/dotted baseline the bars show before any sound arrives.
const IDLE_SCALE = 0.12;

function formatElapsed(seconds) {
  const minutes = Math.floor(seconds / 60);

  return `${minutes}:${`${seconds % 60}`.padStart(2, "0")}`;
}

// ----------------------------------------------------------------------

/**
 * The wide recording indicator, for the places that have room for it (the actions row of a form).
 * A dark pill with a live waveform, the elapsed time and the stop / send buttons, so the user can see
 * that the microphone is picking up their voice and exactly when it stopped listening.
 *
 * The bars are written straight to the DOM from the animation frame: scaleY only touches the
 * compositor, so a moving waveform triggers no React render, no layout and no repaint of the page
 * around it.
 */
export default function VoiceRecorderBar({
  levelRef,
  recording,
  processing,
  statusLabel,
  startedAt,
  onCancel,
  onSend,
}) {
  const { t } = useLocales();
  const isMobile = useResponsive("down", "sm");
  const bars = isMobile ? BAR_COUNT_MOBILE : BAR_COUNT;

  const barsRef = useRef([]);
  const samplesRef = useRef([]);
  const lastSampleRef = useRef(0);

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    samplesRef.current = new Array(bars).fill(0);
  }, [bars]);

  // The clock is polled four times a second but only whole seconds are stored: React bails out when
  // the value is unchanged, so this re-renders at most once per second while staying accurate.
  useEffect(() => {
    if (!recording || !startedAt) return undefined;

    setElapsed(0);

    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt.getTime()) / 1000)),
      250
    );

    return () => clearInterval(id);
  }, [recording, startedAt]);

  useAnimationFrame(time => {
    if (!recording || processing) return;
    if (time - lastSampleRef.current < SAMPLE_INTERVAL_MS) return;
    lastSampleRef.current = time;

    const samples = samplesRef.current;
    samples.push(Math.min(1, (levelRef?.current ?? 0) * 3.5));
    samples.shift();

    barsRef.current.forEach((node, index) => {
      if (node) node.style.transform = `scaleY(${Math.max(IDLE_SCALE, samples[index] ?? 0)})`;
    });
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={theme => ({
        px: 1,
        py: 0.5,
        maxWidth: 1,
        borderRadius: 5,
        color: "common.white",
        bgcolor: theme.palette.mode === "light" ? "grey.900" : "grey.800",
        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        boxShadow: theme.customShadows?.z8,
      })}
    >
      {/* The blinking dot is the "listening" tell that survives even a missing AudioContext. */}
      <Box
        component={m.div}
        aria-hidden
        animate={processing ? { opacity: 0.5 } : { opacity: [1, 0.2, 1] }}
        transition={processing ? { duration: 0.2 } : { duration: 1.2, repeat: Infinity }}
        sx={{
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: processing ? "grey.500" : "error.main",
        }}
      />

      {processing ? (
        <>
          <CircularProgress
            size={14}
            color="inherit"
          />
          <Typography
            variant="caption"
            aria-live="polite"
            noWrap
          >
            {statusLabel}
          </Typography>
        </>
      ) : (
        <>
          <Box
            aria-hidden
            sx={{ display: "flex", alignItems: "center", gap: "2px", height: 24, minWidth: 0 }}
          >
            {Array.from({ length: bars }).map((_, index) => (
              <Box
                key={index}
                ref={node => {
                  barsRef.current[index] = node;
                }}
                sx={{
                  width: 3,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: "common.white",
                  opacity: 0.9,
                  transformOrigin: "center",
                  transform: `scaleY(${IDLE_SCALE})`,
                  transition: "transform 60ms linear",
                }}
              />
            ))}
          </Box>

          <Typography
            variant="caption"
            sx={{ fontVariantNumeric: "tabular-nums", minWidth: 32, textAlign: "right" }}
          >
            {formatElapsed(elapsed)}
          </Typography>

          <Tooltip title={t("cancel")}>
            <IconButton
              size="small"
              color="inherit"
              aria-label={t("cancel")}
              onClick={onCancel}
              sx={theme => ({
                borderRadius: 1,
                bgcolor: alpha(theme.palette.common.white, 0.16),
              })}
            >
              <Iconify
                icon="mdi:stop"
                width={16}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("send")}>
            <IconButton
              size="small"
              aria-label={t("send")}
              onClick={onSend}
              sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
            >
              <Iconify
                icon="mdi:send"
                width={16}
              />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  );
}

VoiceRecorderBar.propTypes = {
  levelRef: PropTypes.object,
  recording: PropTypes.bool,
  processing: PropTypes.bool,
  statusLabel: PropTypes.string,
  startedAt: PropTypes.instanceOf(Date),
  onCancel: PropTypes.func,
  onSend: PropTypes.func,
};
